import React, { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyPlans, usePlanAudit } from "@/hooks/use-plans";
import { useInvoices, useInvoiceStats } from "@/hooks/useInvoices";
import { usePaymentMethods, useDeletePaymentMethod, useSetDefaultPaymentMethod } from "@/hooks/usePaymentMethods";
import { useDownloadFiscalDocument } from "@/hooks/use-download-fiscal-document";

import { useInvoiceCalculations } from "@/hooks/useInvoiceCalculations";
import { usePaymentPreference, PaymentMethod } from "@/hooks/usePaymentPreference";
import { PaymentMethodModal } from "@/components/payments/PaymentMethodModal";
import { PayInvoiceModal } from "@/components/payments/PayInvoiceModal";
import { PixInvoiceModal } from "@/components/payments/PixInvoiceModal";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CreditCard, Calendar, History, ExternalLink, Download, Eye, Plus, Star, Trash2, Settings, QrCode, FileText, Info } from "lucide-react";
import { PLAN_STATUS, SYSTEM_CONFIG } from "@/lib/plans/enforce";
import { formatCurrency, cn } from "@/lib/utils";
import { InvoiceViewModal } from "@/components/admin/InvoiceViewModal";
import { getSystemIcon } from "@/lib/system-icons";

export default function BillingPage() {
  const { activeCompany } = useAuth();
  const company = activeCompany;
  const { data: currentPlans = [] } = useCompanyPlans(company?.id);
  const { data: auditHistory = [] } = usePlanAudit(company?.id);
  const { data: invoices = [] } = useInvoices(company?.id);
  const { data: invoiceStats } = useInvoiceStats(company?.id);
  const { data: paymentMethods = [] } = usePaymentMethods(company?.id);
  const { calculateNextBillingDate } = useInvoiceCalculations();
  const { preference: paymentPreference, updatePreference } = usePaymentPreference(company?.id);
  const downloadFiscalDoc = useDownloadFiscalDocument();
  
  const [activeTab, setActiveTab] = useState("invoices");
  const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPayInvoiceModal, setShowPayInvoiceModal] = useState(false);
  const [showPixModal, setShowPixModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [autoBillingEnabled, setAutoBillingEnabled] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentMethod>(paymentPreference);
  const [autoDownload, setAutoDownload] = useState(false);
  const deletePaymentMethod = useDeletePaymentMethod();
  const setDefaultPaymentMethod = useSetDefaultPaymentMethod();

  // Update selected payment type when preference loads
  React.useEffect(() => {
    setSelectedPaymentType(paymentPreference);
  }, [paymentPreference]);

  // Save preference when payment type changes
  const handlePaymentTypeChange = (value: string) => {
    const method = value as PaymentMethod;
    setSelectedPaymentType(method);
    updatePreference.mutate(method);
  };

  if (!company) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Empresa não encontrada
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalMonthly = currentPlans.reduce((sum, plan) => {
    if (!plan.plan) return sum;
    return sum + (plan.billing_period === 'monthly' ? plan.plan.price_monthly : plan.plan.price_yearly / 12);
  }, 0);

  const handleDeleteCard = async (id: string) => {
    await deletePaymentMethod.mutateAsync({ id, companyId: company.id });
  };

  const handleSetDefault = async (id: string) => {
    await setDefaultPaymentMethod.mutateAsync({ id, companyId: company.id });
  };

  const handlePayInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    if (invoice.payment_method === 'pix' && invoice.pix_qr_code) {
      setShowPixModal(true);
    } else {
      setShowPayInvoiceModal(true);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Faturamento</h1>
        <p className="text-muted-foreground">
          Gerencie suas assinaturas e histórico de pagamentos
        </p>
      </div>

      {/* Current Subscriptions Overview */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Mensal</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalMonthly)}</div>
            <p className="text-xs text-muted-foreground">
              {currentPlans.length} sistema{currentPlans.length !== 1 ? 's' : ''} ativo{currentPlans.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Próxima Cobrança</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentPlans.length > 0 ? calculateNextBillingDate() : '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              Cobrança sempre no dia 10 de cada mês
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status Geral</CardTitle>
            <Badge variant="default">Ativo</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Em dia</div>
            <p className="text-xs text-muted-foreground">
              Todas as assinaturas ativas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Active Subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Assinaturas Ativas</CardTitle>
          <CardDescription>
            Seus planos ativos no PortalONE Suite
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {currentPlans.map((plan, index) => (
              <div key={plan.id}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-primary to-primary/80">
                      {React.createElement(getSystemIcon(plan.system?.code), { 
                        className: "h-5 w-5 text-white" 
                      })}
                    </div>
                    <div>
                      <h3 className="font-semibold">{plan.system?.name || 'Sistema'}</h3>
                      <p className="text-sm text-muted-foreground">
                        {plan.plan?.name || 'Plano'} • Assinatura Mensal
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">
                      {plan.plan ? formatCurrency(
                        plan.billing_period === 'monthly' 
                          ? plan.plan.price_monthly 
                          : plan.plan.price_yearly / 12
                      ) : 'N/A'}
                      <span className="text-sm font-normal text-muted-foreground">
                        /mês
                      </span>
                    </div>
                    <Badge variant="default" className="text-xs">
                      {PLAN_STATUS[plan.status as keyof typeof PLAN_STATUS]?.label || plan.status}
                    </Badge>
                  </div>
                </div>
                {index < currentPlans.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}

            {currentPlans.length === 0 && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Nenhuma assinatura ativa encontrada</p>
                <Button variant="outline" className="mt-4">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ver Planos Disponíveis
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="invoices">Faturas</TabsTrigger>
          <TabsTrigger value="payments">Métodos de Pagamento</TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="space-y-6">
          {/* Payment Method Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Escolha seu Método de Pagamento Preferido</CardTitle>
              <CardDescription>
                Selecione como deseja pagar suas faturas mensais
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={selectedPaymentType} onValueChange={handlePaymentTypeChange}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className={cn("cursor-pointer transition-all", selectedPaymentType === 'card' && "border-primary ring-2 ring-primary")}>
                    <CardContent className="pt-6" onClick={() => handlePaymentTypeChange('card')}>
                      <div className="flex items-center space-x-2 mb-4">
                        <RadioGroupItem value="card" id="card" />
                        <Label htmlFor="card" className="cursor-pointer">
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-5 w-5" />
                            <span className="font-medium">Cartão de Crédito</span>
                          </div>
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Cadastre um cartão para cobranças automáticas
                      </p>
                    </CardContent>
                  </Card>

                  <Card className={cn("cursor-pointer transition-all", selectedPaymentType === 'pix' && "border-primary ring-2 ring-primary")}>
                    <CardContent className="pt-6" onClick={() => handlePaymentTypeChange('pix')}>
                      <div className="flex items-center space-x-2 mb-4">
                        <RadioGroupItem value="pix" id="pix" />
                        <Label htmlFor="pix" className="cursor-pointer">
                          <div className="flex items-center gap-2">
                            <QrCode className="h-5 w-5" />
                            <span className="font-medium">PIX</span>
                          </div>
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Pague suas faturas via PIX com QR Code
                      </p>
                    </CardContent>
                  </Card>

                  <Card className={cn("cursor-pointer transition-all", selectedPaymentType === 'boleto' && "border-primary ring-2 ring-primary")}>
                    <CardContent className="pt-6" onClick={() => handlePaymentTypeChange('boleto')}>
                      <div className="flex items-center space-x-2 mb-4">
                        <RadioGroupItem value="boleto" id="boleto" />
                        <Label htmlFor="boleto" className="cursor-pointer">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            <span className="font-medium">Boleto Bancário</span>
                          </div>
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Pague com boleto bancário (3 dias úteis)
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Conditional content based on payment type */}
          {selectedPaymentType === 'card' && (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    <div>
                      <CardTitle>Configurações de Cobrança</CardTitle>
                      <CardDescription>
                        Configure como deseja gerenciar suas cobranças automáticas
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <label htmlFor="auto-billing" className="text-sm font-medium">
                          Cobrança Automática Ativada
                        </label>
                        {autoBillingEnabled && paymentMethods.some(m => m.is_default) && (
                          <Badge variant="default" className="text-xs">Ativa</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Quando ativado, será usado o cartão padrão para cobranças automáticas
                      </p>
                    </div>
                    <Switch
                      id="auto-billing"
                      checked={autoBillingEnabled}
                      onCheckedChange={setAutoBillingEnabled}
                      disabled={paymentMethods.length === 0}
                    />
                  </div>
                  
                  {paymentMethods.length === 0 && (
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        Adicione um método de pagamento primeiro para habilitar a cobrança automática
                      </p>
                    </div>
                  )}

                  {autoBillingEnabled && paymentMethods.length > 0 && !paymentMethods.some(m => m.is_default) && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        Defina um cartão como padrão abaixo para ativar a cobrança automática
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Cartões Salvos</CardTitle>
                      <CardDescription>
                        Gerencie seus métodos de pagamento salvos
                      </CardDescription>
                    </div>
                    <Button onClick={() => setShowPaymentModal(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      {paymentMethods.length > 0 ? "Adicionar Cartão" : "Adicionar Primeiro Cartão"}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <CreditCard className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{method.card_brand}</p>
                            {method.is_default && <Badge variant="secondary">Padrão</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">•••• •••• •••• {method.card_last_four}</p>
                          <p className="text-xs text-muted-foreground">{method.cardholder_name}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!method.is_default && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSetDefault(method.id)}
                            disabled={setDefaultPaymentMethod.isPending}
                          >
                            <Star className="h-4 w-4 mr-1" />
                            Definir como Padrão
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteCard(method.id)}
                          disabled={deletePaymentMethod.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {paymentMethods.length === 0 && (
                    <div className="text-center py-8">
                      <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Nenhum método de pagamento cadastrado</p>
                      <Button className="mt-4" onClick={() => setShowPaymentModal(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar Primeiro Cartão
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {selectedPaymentType === 'pix' && (
            <Card>
              <CardHeader>
                <CardTitle>Pagamento via PIX</CardTitle>
                <CardDescription>
                  As faturas serão geradas com QR Code e código Copia e Cola para pagamento via PIX
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Como funciona</AlertTitle>
                  <AlertDescription>
                    Quando uma nova fatura for gerada, você receberá:
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>QR Code para pagamento instantâneo</li>
                      <li>Código Copia e Cola para usar no app do seu banco</li>
                      <li>Identificação automática após pagamento</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Método de Pagamento Configurado</p>
                      <p className="text-sm text-muted-foreground">PIX via QR Code</p>
                    </div>
                    <Badge variant="default">Ativo</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedPaymentType === 'boleto' && (
            <Card>
              <CardHeader>
                <CardTitle>Pagamento via Boleto Bancário</CardTitle>
                <CardDescription>
                  As faturas serão geradas com código de barras para pagamento em qualquer banco
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Como funciona</AlertTitle>
                  <AlertDescription>
                    Quando uma nova fatura for gerada, você receberá:
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Código de barras para pagamento em agências bancárias</li>
                      <li>Linha digitável para pagamento em caixas eletrônicos</li>
                      <li>PDF do boleto para impressão</li>
                      <li>Prazo de 3 dias úteis para compensação</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Método de Pagamento Configurado</p>
                      <p className="text-sm text-muted-foreground">Boleto Bancário</p>
                    </div>
                    <Badge variant="default">Ativo</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="invoices" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Faturas</CardTitle>
                  <CardDescription>
                    Histórico de faturas e pagamentos
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Data de Emissão</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">
                        <div className="space-y-1">
                          <div>{invoice.invoice_number}</div>
                          {invoice.invoice_type === 'plan' && invoice.period_start && invoice.period_end && (
                            <div className="text-xs text-muted-foreground">
                              Período: {new Date(invoice.period_start).toLocaleDateString('pt-BR')} - {new Date(invoice.period_end).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{new Date(invoice.issue_date).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>{new Date(invoice.due_date).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>{formatCurrency(invoice.amount_cents / 100)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={
                            invoice.status === 'paid' ? 'default' :
                            invoice.status === 'pending' ? 'secondary' : 'destructive'
                          }>
                            {invoice.status === 'paid' ? 'Pago' :
                             invoice.status === 'pending' ? 'Pendente' : 'Vencido'}
                          </Badge>
                          
                          {invoice.invoice_type && (
                            <Badge variant="outline" className="text-xs">
                              {invoice.invoice_type === 'product' ? 'Compra única' : 'Assinatura'}
                            </Badge>
                          )}
                          
                          {invoice.payment_method && (
                            <Badge variant="outline" className="gap-1">
                              {invoice.payment_method === 'card' && <CreditCard className="h-3 w-3" />}
                              {invoice.payment_method === 'pix' && <QrCode className="h-3 w-3" />}
                              {invoice.payment_method === 'boleto' && <FileText className="h-3 w-3" />}
                              {invoice.payment_method === 'card' ? 'Cartão' : 
                               invoice.payment_method === 'pix' ? 'PIX' : 'Boleto'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          {invoice.status === 'pending' && (
                            <>
                              {invoice.payment_method === 'card' && (
                                <Button 
                                  size="sm"
                                  onClick={() => handlePayInvoice(invoice)}
                                >
                                  Pagar com Cartão
                                </Button>
                              )}
                              
                              {invoice.payment_method === 'pix' && invoice.pix_qr_code && (
                                <Button 
                                  size="sm"
                                  onClick={() => handlePayInvoice(invoice)}
                                >
                                  <QrCode className="h-4 w-4 mr-1" />
                                  Ver QR Code
                                </Button>
                              )}
                              
                              {invoice.payment_method === 'boleto' && invoice.boleto_url && (
                                <Button 
                                  size="sm" 
                                  asChild
                                >
                                  <a href={invoice.boleto_url} target="_blank" rel="noopener noreferrer">
                                    <FileText className="h-4 w-4 mr-1" />
                                    Ver Boleto
                                  </a>
                                </Button>
                              )}
                            </>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setViewInvoiceId(invoice.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setViewInvoiceId(invoice.id);
                              setAutoDownload(true);
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>

                          {invoice.danfe_pdf_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadFiscalDoc.mutate({ 
                                invoiceId: invoice.id, 
                                fileType: 'danfe' 
                              })}
                              disabled={downloadFiscalDoc.isPending}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              DANFE
                            </Button>
                          )}

                          {invoice.xml_file_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadFiscalDoc.mutate({ 
                                invoiceId: invoice.id, 
                                fileType: 'xml' 
                              })}
                              disabled={downloadFiscalDoc.isPending}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              XML
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {invoices.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Nenhuma fatura encontrada</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing History inside Invoices tab */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Histórico de Alterações
                  </CardTitle>
                  <CardDescription>
                    Histórico de mudanças nos seus planos
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Sistema</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Plano Anterior</TableHead>
                    <TableHead>Plano Atual</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditHistory.slice(0, 10).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {new Date(entry.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>{entry.systems.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{entry.action}</Badge>
                      </TableCell>
                      <TableCell>
                        {entry.from_plan?.name || '-'}
                      </TableCell>
                      <TableCell>
                        {entry.to_plan?.name || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {entry.reason || '-'}
                      </TableCell>
                    </TableRow>
                  ))}

                  {auditHistory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Nenhum histórico de alterações encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
      
      {viewInvoiceId && (
        <InvoiceViewModal
          isOpen={!!viewInvoiceId}
          onClose={() => setViewInvoiceId(null)}
          invoiceId={viewInvoiceId}
        />
      )}
      
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        companyId={company.id}
        paymentMethods={paymentMethods}
      />

      {selectedInvoice && (
        <>
          <PayInvoiceModal
            isOpen={showPayInvoiceModal}
            onClose={() => {
              setShowPayInvoiceModal(false);
              setSelectedInvoice(null);
            }}
            invoice={selectedInvoice}
            onPaymentSuccess={() => {
              // Refresh invoices data
              window.location.reload();
            }}
          />

          <PixInvoiceModal
            isOpen={showPixModal}
            onClose={() => {
              setShowPixModal(false);
              setSelectedInvoice(null);
            }}
            invoice={selectedInvoice}
          />
        </>
      )}

      {viewInvoiceId && (
        <InvoiceViewModal
          isOpen={!!viewInvoiceId}
          onClose={() => {
            setViewInvoiceId(null);
            setAutoDownload(false);
          }}
          invoiceId={viewInvoiceId}
          autoDownload={autoDownload}
          invisible={autoDownload}
        />
      )}
    </div>
  );
}
